/* eslint-disable no-console */
/**
 * BrowserCallWidget — CHUB edition (iter 133, Apr 30 2026).
 *
 * With the CHUB integration there's no browser-based audio path — the
 * agent's TeleCMI softphone (desktop/mobile app) handles audio. This
 * widget therefore just provides:
 *
 *   • A floating "Softphone is ringing…" banner the moment a click-to-
 *     call trigger is accepted by CHUB, to remind the agent to pick up
 *     their TeleCMI softphone.
 *   • The `<PhoneStatusPill />` exported for the sidebar, which now
 *     reflects CHUB-credentials-configured status rather than an SDK
 *     connection state.
 *
 * We keep the module path + export names stable so Layout.jsx and
 * App.jsx don't have to change.
 */
import React from "react";
import { Phone, PhoneCall } from "lucide-react";
import { usePiopiy } from "../context/PiopiyContext";

const BrowserCallWidget = () => {
  const { call, hangup } = usePiopiy();
  if (!call) return null;

  const name = call.customerName || call.phone;
  return (
    <div
      className="fixed bottom-4 right-4 z-[60] bg-white border border-amber-300 shadow-lg rounded-2xl p-4 w-[320px]"
      data-testid="browser-call-widget"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center animate-pulse">
          <Phone size={18} className="text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
            Softphone ringing…
          </div>
          <div className="text-sm font-semibold text-slate-800 truncate" title={name}>
            {name}
          </div>
          <div className="text-[11px] text-slate-500">
            Answer on your TeleCMI softphone
          </div>
        </div>
      </div>
      <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
        CHUB is ringing your softphone first. Once you pick up, the call
        dials the customer automatically.
      </div>
      <button
        onClick={hangup}
        className="mt-3 w-full py-2 rounded-lg text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center justify-center gap-1.5"
        data-testid="browser-call-widget-dismiss"
      >
        Dismiss
      </button>
    </div>
  );
};

/**
 * PhoneStatusPill — compact indicator for the sidebar. With CHUB this
 * just reflects whether the agent's credentials are on file. We can't
 * ping the softphone over the network, so the truthful statuses are:
 *
 *   • "CHUB ready"        — credentials saved & verified
 *   • "CHUB not configured" — credentials missing → click to go to Settings
 */
export const PhoneStatusPill = () => {
  const { creds, credsLoading } = usePiopiy();
  if (credsLoading) return null;

  const configured = !!creds?.configured;
  const navigate = () => { window.location.href = "/settings#telecmi"; };

  return (
    <button
      onClick={navigate}
      className={`chub-indicator ${configured ? "chub-indicator--ready" : "chub-indicator--off"}`}
      data-testid="phone-status-pill"
      aria-label={configured ? "Softphone ready" : "Softphone not configured — click to set up"}
    >
      <PhoneCall size={14} />
      <span className={`chub-dot ${configured ? "chub-dot--on" : "chub-dot--off"}`} />
      <span className="rail-tooltip" role="tooltip">
        {configured ? "Softphone ready" : "Softphone — set up in Settings"}
      </span>
    </button>
  );
};

export default BrowserCallWidget;
